// Classifies a club's subscription summary (the exact shape `getMySubscription` returns —
// backend/utils/subscriptionSummary.js's buildSubscriptionSummary) into exactly one of a fixed
// set of display states. ACTIVE is a POSITIVE check (status is literally 'active' AND a real,
// not-yet-passed end date exists) — never the silent "else" every unmatched status used to fall
// into. Anything that matches none of the real states returns 'fallback', so an unexpected status
// combination (e.g. a cancelled/archived club with plan still 'pro', reachable only via
// impersonation since tenant.js blocks normal login for those states) is shown plainly rather
// than misread as "Active" or rendering a blank/negative number.
//
// Pure, no DOM — testable directly. `now` is injectable so tests don't depend on wall-clock time.
export function classifySubscriptionStatus(sub, now = new Date()) {
  if (!sub) {
    return {
      state: 'fallback',
      trialLeft: 0, trialTotal: 14, trialUsed: 0, trialPct: 0,
      daysSinceTrialExpired: null, daysUntilPause: null, daysAgo: 0,
      isExpiringSoon: false, expiresToday: false,
    };
  }

  const isTrialState        = !!sub.isTrial;
  const isTrialExpiredState = sub.status === 'trial_expired';
  const isGraceWindow       = !!sub.isOverdue;
  const isSuspendedState    = sub.status === 'suspended';
  const isPro               = sub.plan === 'pro';
  const isFree              = !isPro;

  // Positive ACTIVE check — the status must literally be 'active' AND a real, not-yet-passed
  // end date must exist. Never inferred by elimination from everything else being false.
  const hasValidActiveEnd = sub.daysRemaining != null && sub.daysRemaining >= 0;
  const isActivePaidState = isPro && sub.status === 'active' && hasValidActiveEnd;
  // The one legitimate "nothing to see here" case: never billed, never trialled, just free.
  const isPlainFreeState  = isFree && sub.status === 'active' && !isTrialState;

  let state;
  if      (isTrialState)        state = 'trial';
  else if (isTrialExpiredState)  state = 'trialExpired';
  else if (isGraceWindow)        state = 'graceWindow';
  else if (isSuspendedState)     state = 'suspended';
  else if (isActivePaidState)    state = 'activePaid';
  else if (isPlainFreeState)     state = 'plainFree';
  else                           state = 'fallback';

  // Trial progress — trialLeft comes from sub.daysRemaining, which buildSubscriptionSummary
  // already computes against trialEndsAt whenever isTrial is true. Not a separate
  // (nonexistent) trialDaysLeft field.
  const trialTotal = 14;
  const trialLeft  = isTrialState ? Math.max(0, sub.daysRemaining ?? 0) : 0;
  const trialUsed  = Math.max(0, trialTotal - trialLeft);
  const trialPct   = Math.min(100, Math.round((trialUsed / trialTotal) * 100));

  // Trial-expired countdown to suspension — DISPLAY ESTIMATE, mirrors the cron's real rule
  // (trial_expired for more than 7 days → suspended, jobs/subscriptionCron.js) without
  // re-deriving the backend's exact KTM-calendar-day math.
  const daysSinceTrialExpired = isTrialExpiredState && sub.trialEndsAt
    ? Math.floor((now.getTime() - new Date(sub.trialEndsAt).getTime()) / 86400000)
    : null;
  const daysUntilPause = daysSinceTrialExpired != null ? Math.max(0, 7 - daysSinceTrialExpired) : null;

  const daysAgo = isSuspendedState && sub.daysRemaining != null ? Math.abs(sub.daysRemaining) : 0;

  const isExpiringSoon = isActivePaidState && sub.daysRemaining <= 7;
  const expiresToday   = isActivePaidState && sub.daysRemaining === 0;

  return {
    state, trialLeft, trialTotal, trialUsed, trialPct,
    daysSinceTrialExpired, daysUntilPause, daysAgo,
    isExpiringSoon, expiresToday,
  };
}
